/**
 * The Alerts queue — the primary screen (Story 14, task 53).
 *
 * Sorted by score on the server (GSI1), filtered by severity as a key
 * range, and by status, disposition and source within the partition. Each
 * row is one alert: its BSM id, its title (the first member's), how many
 * detections it groups, and where it is in the lifecycle. Pages follow the
 * API's opaque cursor. Filters sit in one row above the list, never
 * inside it, and live in the URL (task 88), so the Overview's band tiles
 * link into a filtered queue and Back from an alert returns to it. The
 * date range is a preset in the URL, turned into `since` at request time
 * (task 89); "All statuses" reads both lifecycles (task 90).
 *
 * With no Client selected it is every visible Client's queue merged by
 * score (task 66): each row names its Client, and opening one selects
 * that Client so the detail screen has its context.
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';

import {
  listAlerts,
  listAllAlerts,
  type Alert,
  type AlertListParams,
  type CrossClientAlert,
} from '@bluesphere/api-client';
import { PillButton, SEVERITY_COLORS, SeverityChip } from '@bluesphere/ui-kit';

import { useAccessToken } from '../auth/useAccessToken';
import { useSelectedTenant } from '../auth/useSelectedTenant';
import { orgLabel } from '../lib/orgs';
import {
  SeverityPills,
  SeveritySelect,
  filterSelectClass,
  type SeverityFilterValue,
} from '../components/SeverityFilter';
import { ClientBadge, EmptyState, ErrorState, Loading, SourceBadge } from '../components/states';
import { dispositionLabel, sourceLabel, statusLabel, timeAgo } from '@bluesphere/mdr-alert-panel';
import {
  ALERT_STATUS_OPTIONS,
  DEFAULT_ALERT_STATUS,
  DISPOSITION_OPTIONS,
  RANGE_OPTIONS,
  SERVICE_OPTIONS,
  SOURCE_OPTIONS,
  sinceFor,
  urlValue,
  type AlertsSearch,
} from '../lib/listSearch';

type Row = Alert & Partial<Pick<CrossClientAlert, 'client_tenant_id' | 'display_name'>>;

/**
 * Header and row share one template, or the columns drift apart. On a
 * phone the row is two columns — the alert and its score — and what were
 * the Id, Assets and Status columns fold into the alert's meta line, so
 * nothing needs a sideways scroll (task 75).
 */
const COLS =
  'grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[auto_minmax(0,2.4fr)_1fr_minmax(0,0.9fr)_auto] items-center gap-3';

export function AlertsList() {
  const { tenantId, setTenantId, orgId, selectedOrg } = useSelectedTenant();
  const idToken = useAccessToken();

  const search = useSearch({ from: '/alerts' });
  // `view=all` is the beacon's click-through (task 106): every visible
  // Client's queue, without touching the Client selection.
  const allClients = !tenantId || search.view === 'all';
  // "All of ‹MSP›" narrows the rollup (task 208); the beacon's view=all does not.
  const org = allClients && search.view !== 'all' ? (orgId ?? undefined) : undefined;
  const navigate = useNavigate({ from: '/alerts' });
  const severity: SeverityFilterValue = search.severity ?? 'All';
  const status = search.status ?? DEFAULT_ALERT_STATUS;
  const disposition = search.disposition ?? '';
  const source = search.source ?? '';
  const service = search.service ?? '';
  const range = search.range ?? 'all';

  // `replace`: a filter change is not a history entry, but Back from an
  // alert still lands on the filtered queue.
  const setFilter = (patch: Partial<AlertsSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const clearFilters = () => void navigate({ search: {}, replace: true });

  const query = useInfiniteQuery({
    // The preset, not the timestamp, keys the cache; `since` is computed
    // per request so a refetch moves the window with the clock.
    queryKey: [
      'alerts',
      allClients ? 'all' : tenantId,
      { severity, status, disposition, source, service, range, org },
    ],
    queryFn: ({ pageParam }): Promise<{ items: Row[]; next_cursor: string | null }> => {
      const params: AlertListParams = {
        severity: severity === 'All' ? undefined : severity,
        status,
        disposition: disposition || undefined,
        source: source || undefined,
        service: (service || undefined) as AlertListParams['service'],
        since: sinceFor(range),
        cursor: pageParam,
        limit: 50,
      };
      return allClients
        ? listAllAlerts({ ...params, org }, idToken)
        : listAlerts(tenantId!, params, idToken);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: Boolean(idToken),
    refetchInterval: 60_000,
  });

  const rows: Row[] = query.data?.pages.flatMap((p) => p.items) ?? [];
  const filtered =
    severity !== 'All' ||
    status !== DEFAULT_ALERT_STATUS ||
    Boolean(disposition) ||
    Boolean(source) ||
    Boolean(service) ||
    range !== 'all';
  const setSeverity = (value: SeverityFilterValue) =>
    setFilter({ severity: value === 'All' ? undefined : value });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Alerts</h1>
          {allClients && (
            <p className="text-sm text-white/70">
              Showing {org ? `all of ${orgLabel(selectedOrg)}` : 'all Clients'} · merged by score
            </p>
          )}
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
          {/* On a phone severity is a select like the rest; at sm+ it is the pill rail below. */}
          <SeveritySelect value={severity} onChange={setSeverity} />
          <Select
            label="Status"
            value={status}
            onChange={(v) => setFilter({ status: urlValue(v, DEFAULT_ALERT_STATUS) })}
            options={ALERT_STATUS_OPTIONS}
          />
          <Select
            label="Disposition"
            value={disposition}
            onChange={(v) => setFilter({ disposition: urlValue(v) })}
            options={DISPOSITION_OPTIONS}
          />
          <Select
            label="Source"
            value={source}
            onChange={(v) => setFilter({ source: urlValue(v) })}
            options={SOURCE_OPTIONS}
          />
          <Select
            label="Service"
            value={service}
            onChange={(v) => setFilter({ service: urlValue(v) })}
            options={SERVICE_OPTIONS}
          />
          <Select
            label="Date range"
            value={range}
            onChange={(v) => setFilter({ range: urlValue(v, 'all') as AlertsSearch['range'] })}
            options={RANGE_OPTIONS}
          />
        </div>
      </div>

      <SeverityPills value={severity} onChange={setSeverity} />

      <div className="bs-card p-5">
        <div
          className={`hidden md:grid ${COLS} px-4 py-2 text-xs font-semibold uppercase tracking-wider text-bluesphere-muted`}
        >
          <div className="w-16">Id</div>
          <div>Alert</div>
          <div>Assets</div>
          <div>Status</div>
          <div className="text-right">Score</div>
        </div>

        {query.isPending ? (
          <Loading height="h-40" />
        ) : query.isError ? (
          <ErrorState
            title="Could not load alerts"
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title={filtered ? 'No alerts match these filters' : 'No open alerts'}
            body={
              filtered
                ? 'Nothing matches the current filters.'
                : 'Nothing is waiting for review. Detections the playbook resolved are on the Detections stream.'
            }
            action={
              filtered ? (
                <PillButton variant="outline" size="sm" onClick={clearFilters}>
                  Clear filters
                </PillButton>
              ) : (
                <Link to="/detections" className="text-sm underline">
                  View the Detections stream
                </Link>
              )
            }
          />
        ) : (
          <>
            <div className="bs-scroll max-h-[620px] space-y-1 overflow-y-auto pr-1">
              {rows.map((a) => (
                <AlertRow
                  key={`${a.client_tenant_id ?? tenantId}:${a.alert_id}`}
                  alert={a}
                  showClient={allClients}
                  onOpen={
                    allClients && a.client_tenant_id
                      ? () => setTenantId(a.client_tenant_id as string)
                      : undefined
                  }
                />
              ))}
            </div>
            {query.hasNextPage && (
              <div className="mt-3 flex justify-center">
                <PillButton
                  variant="outline"
                  size="sm"
                  onClick={() => void query.fetchNextPage()}
                  disabled={query.isFetchingNextPage}
                >
                  {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
                </PillButton>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * `onOpen` runs before the navigation on the all-Clients queue: it selects
 * the row's Client, so the detail screen (which reads the selected Client)
 * resolves the BSM id against the right partition.
 */
function AlertRow({
  alert: a,
  showClient = false,
  onOpen,
}: {
  alert: Row;
  showClient?: boolean;
  onOpen?: () => void;
}) {
  return (
    <div className={`bs-row grid ${COLS} px-4 py-2.5`}>
      <span className="hidden w-16 font-mono text-sm font-semibold text-bluesphere-primary md:block">
        {a.short_id}
      </span>
      <div className="min-w-0">
        <Link
          to="/alerts/$alertRef"
          params={{ alertRef: a.short_id }}
          onClick={onOpen}
          className="bs-stretched-link block truncate text-sm font-semibold hover:underline"
        >
          <span className="mr-2 font-mono text-bluesphere-primary md:hidden">{a.short_id}</span>
          {a.title}
        </Link>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {showClient && a.display_name && <ClientBadge name={a.display_name} />}
          <SeverityChip severity={a.severity} />
          {a.sources.map((s) => (
            <SourceBadge key={s} label={sourceLabel(s)} />
          ))}
          <span className="text-xs text-bluesphere-muted" title={a.detected_at}>
            detected {timeAgo(a.detected_at)}
          </span>
          <span className="text-xs text-bluesphere-muted">
            · {a.member_count} detection{a.member_count === 1 ? '' : 's'}
          </span>
          {a.reopen_count > 0 && (
            <span className="text-xs text-bluesphere-muted">· reopened ×{a.reopen_count}</span>
          )}
          {a.backfill && <span className="text-xs text-bluesphere-muted">· back-fill</span>}
        </div>
        {/* The desktop columns, as a second meta line on a phone. */}
        <div className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-bluesphere-muted md:hidden">
          <span className="text-bluesphere-textSecondary">
            {statusLabel(a.status)} · {dispositionLabel(a.disposition)}
          </span>
          {a.asset_names.length > 0 && (
            <span className="min-w-0 truncate" title={a.asset_names.join(', ')}>
              · {a.asset_names.join(', ')}
            </span>
          )}
        </div>
      </div>
      <div
        className="hidden truncate text-sm text-bluesphere-textSecondary md:block"
        title={a.asset_names.join(', ')}
      >
        {a.asset_names.length ? a.asset_names.join(', ') : '—'}
      </div>
      <div className="hidden truncate text-sm text-bluesphere-textSecondary md:block">
        {statusLabel(a.status)}
        <span className="block text-xs text-bluesphere-muted">
          {dispositionLabel(a.disposition)}
        </span>
      </div>
      <div
        className="text-right text-lg font-bold tabular-nums"
        style={{ color: SEVERITY_COLORS[a.severity] }}
      >
        {a.score}
      </div>
    </div>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={filterSelectClass}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value} className="text-bluesphere-textOnLight">
          {o.label}
        </option>
      ))}
    </select>
  );
}
